import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import html2canvas from "html2canvas";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ArrowLeft, Calculator, ChevronRight, ChevronLeft, AlertCircle, AlertTriangle, Download, Plus, FileText, Trash2 } from "lucide-react";
import ProjectSupportDialog from "@/components/ProjectSupportDialog";
import { calculateSundryTotal } from "@/components/SundryItemsManager";
import QuoteTargetSelector from "@/components/QuoteTargetSelector";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ParkingBarriers = ({ selectedQuote, onBack }) => {
  const navigate = useNavigate();
  const [projectName, setProjectName] = useState("");
  const [quoteReference, setQuoteReference] = useState("");
  const [priceModifier, setPriceModifier] = useState("");
  const [userRole, setUserRole] = useState("");
  const [quoteTarget, setQuoteTarget] = useState(""); // "distributor" or "installer"
  const [installerOnlyCapture, setInstallerOnlyCapture] = useState(false);
  
  const [currentStep, setCurrentStep] = useState(1);
  const [numLanes, setNumLanes] = useState("");
  const [chassisType, setChassisType] = useState("");
  const [hasLED, setHasLED] = useState(null);
  const [boomLength, setBoomLength] = useState("");
  const [handed, setHanded] = useState("");
  const [lprCamera, setLprCamera] = useState("none");
  const [safetyRadar, setSafetyRadar] = useState(false);
  const [boomRest, setBoomRest] = useState(false);
  const [loopDetector, setLoopDetector] = useState(false);
  const [warningLight, setWarningLight] = useState(false);
  const [photoDetector, setPhotoDetector] = useState(false);
  const [remoteFobs, setRemoteFobs] = useState(0);
  const [vehiclePostSingle, setVehiclePostSingle] = useState(0);
  const [vehiclePostDouble, setVehiclePostDouble] = useState(0);
  
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [showProjectSupport, setShowProjectSupport] = useState(false);
  const [supportDeclined, setSupportDeclined] = useState(false);
  
  // Sundry Items
  const [sundryItems, setSundryItems] = useState([]);
  const [showSundryDialog, setShowSundryDialog] = useState(false);
  const [editingSundryIndex, setEditingSundryIndex] = useState(null);
  const [sundryForm, setSundryForm] = useState({
    sku: '',
    description: '',
    quantity: 1,
    unit_price: 0
  });
  
  // Quote box ref for download
  const quoteBoxRef = useRef(null);

  // Sundry Items Functions
  const handleAddSundryItem = () => {
    setSundryForm({ sku: '', description: '', quantity: 1, unit_price: 0 });
    setEditingSundryIndex(null);
    setShowSundryDialog(true);
  };

  const handleEditSundryItem = (index) => {
    setSundryForm(sundryItems[index]);
    setEditingSundryIndex(index);
    setShowSundryDialog(true);
  };

  const handleSaveSundryItem = () => {
    if (!sundryForm.description || sundryForm.quantity <= 0) {
      setError('Please enter a description and valid quantity');
      return;
    }
    
    const newItem = {
      ...sundryForm,
      quantity: parseFloat(sundryForm.quantity) || 1,
      unit_price: parseFloat(sundryForm.unit_price) || 0,
      line_total: (parseFloat(sundryForm.quantity) || 1) * (parseFloat(sundryForm.unit_price) || 0)
    };
    
    if (editingSundryIndex !== null) {
      const updated = [...sundryItems];
      updated[editingSundryIndex] = newItem;
      setSundryItems(updated);
    } else {
      setSundryItems([...sundryItems, newItem]);
    }
    
    setShowSundryDialog(false);
    setSundryForm({ sku: '', description: '', quantity: 1, unit_price: 0 });
    setEditingSundryIndex(null);
  };

  const handleDeleteSundryItem = (index) => {
    if (window.confirm('Remove this sundry item?')) {
      setSundryItems(sundryItems.filter((_, i) => i !== index));
    }
  };

  const handleDownloadQuoteImage = async (installerOnly = false) => {
    if (!quoteBoxRef.current) return;
    try {
      if (installerOnly) {
        setInstallerOnlyCapture(true);
        await new Promise(r => setTimeout(r, 250));
      }
      const canvas = await html2canvas(quoteBoxRef.current, {
        scale: 2,
        backgroundColor: '#ffffff',
        logging: false
      });
      const suffix = installerOnly ? 'Installer' : (isDistributorUser ? 'Distributor' : '');
      const link = document.createElement('a');
      link.download = `${projectName || 'ParkingBarrier'}${suffix ? '_' + suffix : ''}_Quote.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (err) {
      console.error('Error downloading quote:', err);
      setError('Failed to download quote image');
    } finally {
      if (installerOnly) setInstallerOnlyCapture(false);
    }
  };

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  });

  // Get user role on mount
  useEffect(() => {
    const user = localStorage.getItem('user');
    if (user) {
      try {
        const userData = JSON.parse(user);
        setUserRole(userData.role || '');
      } catch (e) {
        setUserRole('');
      }
    }
  }, []);

  // Load selected quote on mount
  useEffect(() => {
    if (selectedQuote) {
      // Load the saved quote data
      setProjectName(selectedQuote.project_name || "");
      setQuoteReference(selectedQuote.quote_reference || "");
      setPriceModifier(selectedQuote.price_modifier || "");
      
      // Set result to show the saved quote
      if (selectedQuote.bom) {
        setResult({
          bom: selectedQuote.bom,
          subtotal: selectedQuote.subtotal,
          total_delivery: selectedQuote.total_delivery,
          total: selectedQuote.total,
          installer_subtotal: selectedQuote.installer_subtotal,
          installer_delivery: selectedQuote.installer_delivery,
          installer_total: selectedQuote.installer_total,
          distributor_margin: selectedQuote.distributor_margin,
          margin_percentage: selectedQuote.margin_percentage
        });
      }
      
      // Load lane configuration if available
      if (selectedQuote.lanes && selectedQuote.lanes.length > 0) {
        setNumLanes(selectedQuote.lanes.length.toString());
        const firstLane = selectedQuote.lanes[0];
        setChassisType(firstLane.chassisType || "");
        setHasLED(firstLane.hasLED);
        setBoomLength(firstLane.boomLength || "");
        setHanded(firstLane.handed || "");
        setLprCamera(firstLane.lprCamera || "none");
        
        const acc = firstLane.accessories || {};
        setSafetyRadar(acc.safetyRadar || false);
        setBoomRest(acc.boomRest || false);
        setLoopDetector(acc.loopDetector || false);
        setWarningLight(acc.warningLight || false);
        setPhotoDetector(acc.photoDetector || false);
        setRemoteFobs(acc.remoteFobs || 0);
        setVehiclePostSingle(acc.vehiclePostSingle || 0);
        setVehiclePostDouble(acc.vehiclePostDouble || 0);
      }
    }
  }, [selectedQuote]);

  // BOM pricing visibility flags
  const isAdmin = userRole === 'admin' || userRole === 'zkteco_admin' || userRole === 'zkteco';
  const isDistributorUser = userRole === 'distributor' || userRole === 'distributor_admin';
  const showBomDistributorPrice = !installerOnlyCapture && ((isAdmin && quoteTarget === 'distributor') || isDistributorUser);
  const showBomInstallerPrice = isAdmin ? (quoteTarget === 'distributor' || quoteTarget === 'installer') : (isDistributorUser || userRole === 'installer');
  const showBomEndUserPrice = isAdmin && quoteTarget === 'end_user';
  const hasInstallerPricing = result?.bom?.[0]?.installer_unit_price;
  const hasEndUserPricing = result?.bom?.[0]?.end_user_unit_price;

  const getBoomLengthOptions = () => {
    if (chassisType === "skirted") {
      return ["3", "4"];
    }
    if (chassisType === "folding") {
      return ["3"];
    }
    return ["3", "4.5", "6"];
  };

  const canProceed = () => {
    switch(currentStep) {
      case 1: return numLanes !== "" && parseInt(numLanes) > 0;
      case 2: return chassisType !== "";
      case 3: return hasLED !== null;
      case 4: return boomLength !== "";
      case 5: return handed !== "";
      case 6: return true; // Camera is optional
      case 7: return true; // Safety Radar is optional
      case 8: return true; // Boom Rest is optional
      case 9: return true; // Ground Loop is optional
      case 10: return true; // Other accessories are optional
      default: return false;
    }
  };

  const nextStep = () => {
    if (currentStep < 10) {
      // Skip step 3 (LED) if chassis type is skirted (no LED versions available)
      if (currentStep === 2 && chassisType === "skirted") {
        setHasLED(false); // Auto-set to false for skirted barriers
        setCurrentStep(4); // Skip to step 4 (Boom Length)
      } else {
        setCurrentStep(currentStep + 1);
      }
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      // Skip step 3 (LED) when going back from step 4 if chassis type is skirted
      if (currentStep === 4 && chassisType === "skirted") {
        setCurrentStep(2); // Go back to step 2 (Chassis Type)
      } else {
        setCurrentStep(currentStep - 1);
      }
    }
  };

  const handleCalculate = async () => {
    setError("");
    setSuccess("");
    
    if (!projectName) {
      setError("Please enter a project name");
      return;
    }

    if (isAdmin && !quoteTarget) {
      setError("Please select who this quote is for (Distributor, Installer, or End User)");
      return;
    }

    if (!numLanes || parseInt(numLanes) <= 0) {
      setError("Please enter number of lanes");
      return;
    }

    setLoading(true);

    try {
      const lanes = [];
      for (let i = 0; i < parseInt(numLanes); i++) {
        lanes.push({
          chassisType,
          hasLED,
          // Map boom length: for skirted barriers, "4" displays as "4m" to users but sends "4.5" to backend
          boomLength: chassisType === "skirted" && boomLength === "4" ? "4.5" : boomLength,
          handed,
          lprCamera,
          accessories: {
            boomRest,
            safetyRadar,
            warningLight,
            loopDetector,
            photoDetector,
            remoteFobs,
            vehiclePostSingle,
            vehiclePostDouble
          }
        });
      }

      const response = await axios.post(
        `${API}/configure-parking-barriers`,
        {
          project_name: projectName,
          quote_reference: quoteReference,
          price_modifier: parseFloat(priceModifier) || 0,
          lanes: lanes
        },
        getAuthHeaders()
      );

      setResult(response.data);
      setSuccess("Configuration calculated successfully!");
      setTimeout(() => setSuccess(""), 3000);

      // Check if installer price >= £20,000 for project support (only show once per quote)
      const installerTotal = response.data.installer_total || 0;
      if (installerTotal >= 20000 && !supportDeclined) {
        setShowProjectSupport(true);
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Error calculating configuration");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-yellow-50 p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        <Button variant="outline" onClick={() => navigate('/dashboard')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>

        <Card>
          <CardHeader className="bg-gradient-to-r from-orange-600 to-yellow-600 text-white">
            <CardTitle className="text-3xl">Parking Barriers Configurator</CardTitle>
            <CardDescription className="text-orange-100">
              Configure Parking Barrier Systems
            </CardDescription>
          </CardHeader>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Project Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="project-name">Project Name *</Label>
                <Input id="project-name" value={projectName} onChange={(e) => setProjectName(e.target.value)} placeholder="Enter project name" />
              </div>
              <div>
                <Label htmlFor="quote-ref">Quote Reference</Label>
                <Input id="quote-ref" value={quoteReference} onChange={(e) => setQuoteReference(e.target.value)} placeholder="Optional reference" />
              </div>
              <div>
                <Label htmlFor="price-modifier">Installer Discount (%)</Label>
                <Input id="price-modifier" type="number" value={priceModifier} onChange={(e) => setPriceModifier(e.target.value)} placeholder="0" />
              </div>
            </div>
            
            {/* Quote Target Selector - ZKTeco Staff Only */}
            <QuoteTargetSelector 
              quoteTarget={quoteTarget} 
              setQuoteTarget={setQuoteTarget} 
              userRole={userRole} 
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Configuration</CardTitle>
            <CardDescription>All lanes will be configured identically</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Progress Indicator */}
            <div className="flex items-center justify-between text-sm text-slate-600">
              <span>Step {currentStep} of 10</span>
              <span>{Math.round((currentStep / 10) * 100)}% Complete</span>
            </div>
            <div className="w-full bg-slate-200 rounded-full h-2">
              <div 
                className="bg-orange-600 h-2 rounded-full transition-all"
                style={{ width: `${(currentStep / 10) * 100}%` }}
              />
            </div>

            {/* Step 1: Number of Lanes */}
            {currentStep === 1 && (
              <div className="space-y-4">
                <Label className="text-lg font-semibold">Step 1: Number of Lanes</Label>
                <Input
                  type="number"
                  min="1"
                  value={numLanes}
                  onChange={(e) => setNumLanes(e.target.value)}
                  placeholder="Enter number of lanes"
                  className="text-lg"
                />
                {numLanes && parseInt(numLanes) > 0 && (
                  <p className="text-sm text-green-600">✓ {numLanes} lane{parseInt(numLanes) > 1 ? 's' : ''} - all configured identically</p>
                )}
              </div>
            )}

            {/* Step 2: Chassis Type */}
            {currentStep === 2 && (
              <div className="space-y-4">
                <Label className="text-lg font-semibold">Step 2: Select Chassis Type</Label>
                <RadioGroup value={chassisType} onValueChange={setChassisType}>
                  <div 
                    className="flex items-center space-x-3 p-4 border-2 rounded hover:bg-slate-50 cursor-pointer"
                    onClick={() => setChassisType("standard")}
                  >
                    <RadioGroupItem value="standard" id="standard" />
                    <Label htmlFor="standard" className="cursor-pointer flex-1">
                      <div className="font-medium">Standard</div>
                      <div className="text-sm text-slate-600">Regular parking barrier chassis</div>
                    </Label>
                  </div>
                  <div 
                    className="flex items-center space-x-3 p-4 border-2 rounded hover:bg-slate-50 cursor-pointer"
                    onClick={() => setChassisType("folding")}
                  >
                    <RadioGroupItem value="folding" id="folding" />
                    <Label htmlFor="folding" className="cursor-pointer flex-1">
                      <div className="font-medium">Folding</div>
                      <div className="text-sm text-slate-600">90-degree folding boom chassis</div>
                    </Label>
                  </div>
                  <div 
                    className="flex items-center space-x-3 p-4 border-2 rounded hover:bg-slate-50 cursor-pointer"
                    onClick={() => setChassisType("skirted")}
                  >
                    <RadioGroupItem value="skirted" id="skirted" />
                    <Label htmlFor="skirted" className="cursor-pointer flex-1">
                      <div className="font-medium">Skirted (Fence Barrier)</div>
                      <div className="text-sm text-slate-600">Chassis with protective skirting - includes boom (3m or 4m only)</div>
                    </Label>
                  </div>
                </RadioGroup>
                {chassisType === "skirted" && (
                  <Alert className="bg-amber-50 border-amber-300">
                    <AlertTriangle className="h-4 w-4 text-amber-600" />
                    <AlertDescription className="text-amber-900">
                      <strong>Please be advised:</strong> Extended lead time on fence barriers as they are ordered direct from factory. LED option is not available for skirted barriers.
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            )}

            {/* Step 3: LED */}
            {currentStep === 3 && (
              <div className="space-y-4">
                <Label className="text-lg font-semibold">Step 3: Safety LED Lighting</Label>
                <RadioGroup value={hasLED === true ? "yes" : hasLED === false ? "no" : ""} 
                            onValueChange={(val) => setHasLED(val === "yes")}>
                  <div 
                    className="flex items-center space-x-3 p-4 border-2 rounded hover:bg-slate-50 cursor-pointer"
                    onClick={() => setHasLED(true)}
                  >
                    <RadioGroupItem value="yes" id="led-yes" />
                    <Label htmlFor="led-yes" className="cursor-pointer flex-1">
                      <div className="font-medium">Yes - Include Safety LED</div>
                      <div className="text-sm text-slate-600">Boom with integrated Safety LED lighting</div>
                    </Label>
                  </div>
                  <div 
                    className="flex items-center space-x-3 p-4 border-2 rounded hover:bg-slate-50 cursor-pointer"
                    onClick={() => setHasLED(false)}
                  >
                    <RadioGroupItem value="no" id="led-no" />
                    <Label htmlFor="led-no" className="cursor-pointer flex-1">
                      <div className="font-medium">No - Standard Boom</div>
                      <div className="text-sm text-slate-600">Boom without Safety LED lighting</div>
                    </Label>
                  </div>
                </RadioGroup>
              </div>
            )}

            {/* Step 4: Boom Length */}
            {currentStep === 4 && (
              <div className="space-y-4">
                <Label className="text-lg font-semibold">Step 4: Boom Length</Label>
                <RadioGroup value={boomLength} onValueChange={setBoomLength}>
                  {getBoomLengthOptions().map(length => (
                    <div 
                      key={length} 
                      className="flex items-center space-x-3 p-4 border-2 rounded hover:bg-slate-50 cursor-pointer"
                      onClick={() => setBoomLength(length)}
                    >
                      <RadioGroupItem value={length} id={`${length}m`} />
                      <Label htmlFor={`${length}m`} className="cursor-pointer flex-1">
                        <div className="font-medium">{length}m</div>
                        <div className="text-sm text-slate-600">{length} metre boom length</div>
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>
            )}

            {/* Step 5: Handed */}
            {currentStep === 5 && (
              <div className="space-y-4">
                <Label className="text-lg font-semibold">Step 5: Barrier Handed</Label>
                <RadioGroup value={handed} onValueChange={setHanded}>
                  <div 
                    className="flex items-center space-x-3 p-4 border-2 rounded hover:bg-slate-50 cursor-pointer"
                    onClick={() => setHanded("L")}
                  >
                    <RadioGroupItem value="L" id="left" />
                    <Label htmlFor="left" className="cursor-pointer flex-1">
                      <div className="font-medium">Left Handed</div>
                      <div className="text-sm text-slate-600">Body to the left, boom to the right</div>
                    </Label>
                  </div>
                  <div 
                    className="flex items-center space-x-3 p-4 border-2 rounded hover:bg-slate-50 cursor-pointer"
                    onClick={() => setHanded("R")}
                  >
                    <RadioGroupItem value="R" id="right" />
                    <Label htmlFor="right" className="cursor-pointer flex-1">
                      <div className="font-medium">Right Handed</div>
                      <div className="text-sm text-slate-600">Body to the right, boom to the left</div>
                    </Label>
                  </div>
                </RadioGroup>
              </div>
            )}

            {/* Step 6: LPR Camera */}
            {currentStep === 6 && (
              <div className="space-y-4">
                <Label className="text-lg font-semibold">Step 6: LPR Camera (Optional)</Label>
                <RadioGroup value={lprCamera} onValueChange={setLprCamera}>
                  <div 
                    className="flex items-center space-x-3 p-4 border-2 rounded hover:bg-slate-50 cursor-pointer"
                    onClick={() => setLprCamera("none")}
                  >
                    <RadioGroupItem value="none" id="no-camera" />
                    <Label htmlFor="no-camera" className="cursor-pointer flex-1">
                      <div className="font-medium">No Camera</div>
                      <div className="text-sm text-slate-600">No license plate recognition</div>
                    </Label>
                  </div>
                  <div 
                    className="flex items-center space-x-3 p-4 border-2 rounded hover:bg-slate-50 cursor-pointer"
                    onClick={() => setLprCamera("bullet")}
                  >
                    <RadioGroupItem value="bullet" id="bullet" />
                    <Label htmlFor="bullet" className="cursor-pointer flex-1">
                      <div className="font-medium">Bullet Camera</div>
                      <div className="text-sm text-slate-600">Bullet-style LPR camera</div>
                    </Label>
                  </div>
                  <div 
                    className="flex items-center space-x-3 p-4 border-2 rounded hover:bg-slate-50 cursor-pointer"
                    onClick={() => setLprCamera("dome")}
                  >
                    <RadioGroupItem value="dome" id="dome" />
                    <Label htmlFor="dome" className="cursor-pointer flex-1">
                      <div className="font-medium">Dome Camera</div>
                      <div className="text-sm text-slate-600">Dome-style LPR camera</div>
                    </Label>
                  </div>
                </RadioGroup>
              </div>
            )}

            {/* Step 7: Safety Radar */}
            {currentStep === 7 && (
              <div className="space-y-4">
                <Label className="text-lg font-semibold">Step 7: Safety Radar (Optional)</Label>
                <p className="text-sm text-slate-600">Detects obstacles under the boom for additional safety</p>
                <RadioGroup value={safetyRadar ? "yes" : "no"} onValueChange={(val) => setSafetyRadar(val === "yes")}>
                  <div 
                    className="flex items-center space-x-3 p-4 border-2 rounded hover:bg-slate-50 cursor-pointer"
                    onClick={() => setSafetyRadar(true)}
                  >
                    <RadioGroupItem value="yes" id="radar-yes" />
                    <Label htmlFor="radar-yes" className="cursor-pointer flex-1">
                      <div className="font-medium">Yes - Include Safety Radar</div>
                      <div className="text-sm text-slate-600">Recommended for high-traffic areas</div>
                    </Label>
                  </div>
                  <div 
                    className="flex items-center space-x-3 p-4 border-2 rounded hover:bg-slate-50 cursor-pointer"
                    onClick={() => setSafetyRadar(false)}
                  >
                    <RadioGroupItem value="no" id="radar-no" />
                    <Label htmlFor="radar-no" className="cursor-pointer flex-1">
                      <div className="font-medium">No - Skip Safety Radar</div>
                    </Label>
                  </div>
                </RadioGroup>
              </div>
            )}

            {/* Step 8: Boom Rest */}
            {currentStep === 8 && (
              <div className="space-y-4">
                <Label className="text-lg font-semibold">Step 8: Boom Rest Y Stand (Optional)</Label>
                <p className="text-sm text-slate-600">Support stand to hold boom in lowered position when not in use</p>
                <RadioGroup value={boomRest ? "yes" : "no"} onValueChange={(val) => setBoomRest(val === "yes")}>
                  <div 
                    className="flex items-center space-x-3 p-4 border-2 rounded hover:bg-slate-50 cursor-pointer"
                    onClick={() => setBoomRest(true)}
                  >
                    <RadioGroupItem value="yes" id="rest-yes" />
                    <Label htmlFor="rest-yes" className="cursor-pointer flex-1">
                      <div className="font-medium">Yes - Include Boom Rest</div>
                      <div className="text-sm text-slate-600">Keeps boom secure when lowered</div>
                    </Label>
                  </div>
                  <div 
                    className="flex items-center space-x-3 p-4 border-2 rounded hover:bg-slate-50 cursor-pointer"
                    onClick={() => setBoomRest(false)}
                  >
                    <RadioGroupItem value="no" id="rest-no" />
                    <Label htmlFor="rest-no" className="cursor-pointer flex-1">
                      <div className="font-medium">No - Skip Boom Rest</div>
                    </Label>
                  </div>
                </RadioGroup>
              </div>
            )}

            {/* Step 9: Ground Loop Detector */}
            {currentStep === 9 && (
              <div className="space-y-4">
                <Label className="text-lg font-semibold">Step 9: Ground Loop Detector (Optional)</Label>
                <p className="text-sm text-slate-600">Detects vehicles passing through the barrier (includes 100m cable)</p>
                <RadioGroup value={loopDetector ? "yes" : "no"} onValueChange={(val) => setLoopDetector(val === "yes")}>
                  <div 
                    className="flex items-center space-x-3 p-4 border-2 rounded hover:bg-slate-50 cursor-pointer"
                    onClick={() => setLoopDetector(true)}
                  >
                    <RadioGroupItem value="yes" id="loop-yes" />
                    <Label htmlFor="loop-yes" className="cursor-pointer flex-1">
                      <div className="font-medium">Yes - Include Ground Loop Detector</div>
                      <div className="text-sm text-slate-600">Automatic detection with 100m cable included</div>
                    </Label>
                  </div>
                  <div 
                    className="flex items-center space-x-3 p-4 border-2 rounded hover:bg-slate-50 cursor-pointer"
                    onClick={() => setLoopDetector(false)}
                  >
                    <RadioGroupItem value="no" id="loop-no" />
                    <Label htmlFor="loop-no" className="cursor-pointer flex-1">
                      <div className="font-medium">No - Skip Ground Loop Detector</div>
                    </Label>
                  </div>
                </RadioGroup>
              </div>
            )}

            {/* Step 10: Other Accessories */}
            {currentStep === 10 && (
              <div className="space-y-4">
                <Label className="text-lg font-semibold">Step 10: Additional Accessories (Optional)</Label>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3 p-3 border rounded hover:bg-slate-50">
                    <Checkbox
                      id="warning-light"
                      checked={warningLight}
                      onCheckedChange={setWarningLight}
                    />
                    <Label htmlFor="warning-light" className="cursor-pointer flex-1">Warning Light</Label>
                  </div>
                  <div className="flex items-center space-x-3 p-3 border rounded hover:bg-slate-50">
                    <Checkbox
                      id="photo-detector"
                      checked={photoDetector}
                      onCheckedChange={setPhotoDetector}
                    />
                    <Label htmlFor="photo-detector" className="cursor-pointer flex-1">Photo Electric Detector</Label>
                  </div>
                  <div className="p-3 border rounded">
                    <Label htmlFor="remote-fobs" className="mb-2 block">Remote Fobs (Quantity per lane)</Label>
                    <Input
                      id="remote-fobs"
                      type="number"
                      min="0"
                      value={remoteFobs}
                      onChange={(e) => setRemoteFobs(parseInt(e.target.value) || 0)}
                      placeholder="0"
                      className="w-32"
                    />
                  </div>
                  <div className="p-3 border rounded">
                    <Label htmlFor="vehicle-post-single" className="mb-2 block">Car Height Vehicle Post 1.2m (Quantity per lane)</Label>
                    <Input
                      id="vehicle-post-single"
                      type="number"
                      min="0"
                      value={vehiclePostSingle}
                      onChange={(e) => setVehiclePostSingle(parseInt(e.target.value) || 0)}
                      placeholder="0"
                      className="w-32"
                    />
                  </div>
                  <div className="p-3 border rounded">
                    <Label htmlFor="vehicle-post-double" className="mb-2 block">Car and Wagon Vehicle Post 2m (Quantity per lane)</Label>
                    <Input
                      id="vehicle-post-double"
                      type="number"
                      min="0"
                      value={vehiclePostDouble}
                      onChange={(e) => setVehiclePostDouble(parseInt(e.target.value) || 0)}
                      placeholder="0"
                      className="w-32"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex gap-3 pt-4">
              {currentStep > 1 && (
                <Button variant="outline" onClick={prevStep} className="flex-1">
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
              )}
              {currentStep < 10 && (
                <Button onClick={nextStep} disabled={!canProceed()} className="flex-1 bg-orange-600 hover:bg-orange-700">
                  Next
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {currentStep === 10 && (
          <Card>
            <CardContent className="pt-6">
              {error && (
                <Alert variant="destructive" className="mb-4">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              {success && (
                <Alert className="mb-4 bg-green-50 text-green-800 border-green-200">
                  <AlertDescription>{success}</AlertDescription>
                </Alert>
              )}
              <Button onClick={handleCalculate} disabled={loading} className="w-full bg-orange-600 hover:bg-orange-700" size="lg">
                {loading ? "Calculating..." : (
                  <>
                    <Calculator className="w-5 h-5 mr-2" />
                    Calculate & Save
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        )}

        {result && (
          <div ref={quoteBoxRef}>
          <Card>
            <CardHeader className="bg-orange-50 flex flex-row items-center justify-between">
              <CardTitle>Configuration Results - {numLanes} Lane{parseInt(numLanes) > 1 ? 's' : ''}</CardTitle>
              <div className="flex gap-2" data-html2canvas-ignore="true">
                {isDistributorUser ? (
                  <>
                    <Button 
                      variant="outline" 
                      size="sm"
                      data-testid="download-distributor-quote-btn"
                      onClick={() => handleDownloadQuoteImage(false)}
                      className="flex items-center gap-2"
                    >
                      <Download className="h-4 w-4" />
                      Distributor Quote
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      data-testid="download-installer-quote-btn"
                      onClick={() => handleDownloadQuoteImage(true)}
                      className="flex items-center gap-2"
                    >
                      <Download className="h-4 w-4" />
                      Installer Quote
                    </Button>
                  </>
                ) : (
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => handleDownloadQuoteImage(false)}
                    className="flex items-center gap-2"
                  >
                    <Download className="h-4 w-4" />
                    Download Quote
                  </Button>
                )}
                <Button 
                  variant="outline"
                  size="sm"
                  onClick={handleAddSundryItem}
                  className="flex items-center gap-2"
                >
                  <Plus className="h-4 w-4" />
                  Add Sundry Line
                </Button>
              </div>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 px-2">SKU</th>
                      <th className="text-left py-2 px-2">Description</th>
                      <th className="text-right py-2 px-2">Qty</th>
                      <th className="text-right py-2 px-2">{installerOnlyCapture ? 'Unit Price' : showBomDistributorPrice ? 'Dist. Unit' : 'Unit Price'}</th>
                      <th className="text-right py-2 px-2">{installerOnlyCapture ? 'Total' : showBomDistributorPrice ? 'Dist. Total' : 'Total'}</th>
                      {(showBomInstallerPrice && hasInstallerPricing && !installerOnlyCapture) && (
                        <>
                          <th className="text-right py-2 px-2 bg-green-50 text-green-800">{userRole === 'installer' ? 'Unit Price' : 'Inst. Unit'}</th>
                          <th className="text-right py-2 px-2 bg-green-50 text-green-800">{userRole === 'installer' ? 'Total' : 'Inst. Total'}</th>
                        </>
                      )}
                      {(showBomEndUserPrice && hasEndUserPricing) && (
                        <>
                          <th className="text-right py-2 px-2 bg-orange-50 text-orange-800">EU Unit</th>
                          <th className="text-right py-2 px-2 bg-orange-50 text-orange-800">EU Total</th>
                        </>
                      )}
                      <th className="text-center py-2 px-2 print:hidden"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.bom?.map((item, index) => (
                      <tr key={index} className="border-b">
                        <td className="py-2 px-2 font-medium">{item.sku}</td>
                        <td className="py-2 px-2">{item.description}</td>
                        <td className="py-2 px-2 text-right">{item.quantity}</td>
                        <td className="py-2 px-2 text-right">£{(installerOnlyCapture ? item.installer_unit_price : item.unit_price)?.toFixed(2)}</td>
                        <td className="py-2 px-2 text-right font-medium">£{(installerOnlyCapture ? item.installer_line_total : item.line_total)?.toFixed(2)}</td>
                        {(showBomInstallerPrice && item.installer_unit_price && !installerOnlyCapture) && (
                          <>
                            <td className="py-2 px-2 text-right bg-green-50 text-green-700">£{item.installer_unit_price?.toFixed(2)}</td>
                            <td className="py-2 px-2 text-right font-medium bg-green-50 text-green-700">£{item.installer_line_total?.toFixed(2)}</td>
                          </>
                        )}
                        {(showBomEndUserPrice && item.end_user_unit_price) && (
                          <>
                            <td className="py-2 px-2 text-right bg-orange-50 text-orange-700">£{item.end_user_unit_price?.toFixed(2)}</td>
                            <td className="py-2 px-2 text-right font-medium bg-orange-50 text-orange-700">£{item.end_user_line_total?.toFixed(2)}</td>
                          </>
                        )}
                        <td className="print:hidden"></td>
                      </tr>
                    ))}
                    {/* Sundry Items in B.O.M */}
                    {sundryItems.map((item, index) => (
                      <tr key={`sundry-${index}`} className="border-b border-amber-200 bg-amber-50">
                        <td className="py-2 px-2 font-medium text-amber-800">{item.sku || 'SUNDRY'}</td>
                        <td className="py-2 px-2 text-amber-800">{item.description}</td>
                        <td className="py-2 px-2 text-right text-amber-900">{item.quantity}</td>
                        <td className="py-2 px-2 text-right text-amber-900">£{item.unit_price?.toFixed(2)}</td>
                        <td className="py-2 px-2 text-right font-medium text-amber-900">£{item.line_total?.toFixed(2)}</td>
                        {(showBomInstallerPrice && hasInstallerPricing && !installerOnlyCapture) && (
                          <>
                            <td className="py-2 px-2 text-right bg-amber-100 text-amber-700">£{item.unit_price?.toFixed(2)}</td>
                            <td className="py-2 px-2 text-right font-medium bg-amber-100 text-amber-700">£{item.line_total?.toFixed(2)}</td>
                          </>
                        )}
                        {(showBomEndUserPrice && hasEndUserPricing) && (
                          <>
                            <td className="py-2 px-2 text-right bg-orange-100 text-orange-700">£{item.unit_price?.toFixed(2)}</td>
                            <td className="py-2 px-2 text-right font-medium bg-orange-100 text-orange-700">£{item.line_total?.toFixed(2)}</td>
                          </>
                        )}
                        <td className="py-2 px-2 text-center print:hidden">
                          <div className="flex justify-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0 text-amber-700 hover:text-amber-900"
                              onClick={() => handleEditSundryItem(index)}
                            >
                              <FileText className="h-3 w-3" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0 text-red-600 hover:text-red-700"
                              onClick={() => handleDeleteSundryItem(index)}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 bg-orange-50">
                      <td colSpan="4" className="py-3 px-2 text-right font-semibold">Subtotal (exc. delivery):</td>
                      <td className="py-3 px-2 text-right font-bold">£{(installerOnlyCapture ? result.installer_subtotal : result.subtotal)?.toFixed(2)}</td>
                      {(showBomInstallerPrice && hasInstallerPricing && !installerOnlyCapture) && (
                        <>
                          <td className="py-3 px-2 text-right bg-green-50"></td>
                          <td className="py-3 px-2 text-right font-bold bg-green-50 text-green-700">£{result.installer_subtotal?.toFixed(2)}</td>
                        </>
                      )}
                      {(showBomEndUserPrice && hasEndUserPricing) && (
                        <>
                          <td className="py-3 px-2 text-right bg-orange-50"></td>
                          <td className="py-3 px-2 text-right font-bold bg-orange-50 text-orange-700">
                            £{result.bom?.reduce((sum, item) => sum + (item.end_user_line_total || 0), 0).toFixed(2)}
                          </td>
                        </>
                      )}
                    </tr>
                    <tr className="bg-orange-50">
                      <td colSpan="4" className="py-3 px-2 text-right font-semibold">Delivery to Mainland GB:</td>
                      <td className="py-3 px-2 text-right font-bold">£{(installerOnlyCapture ? result.installer_delivery : result.total_delivery)?.toFixed(2)}</td>
                      {(showBomInstallerPrice && hasInstallerPricing && !installerOnlyCapture) && (
                        <>
                          <td className="py-3 px-2 text-right bg-green-50"></td>
                          <td className="py-3 px-2 text-right font-bold bg-green-50 text-green-700">£{result.installer_delivery?.toFixed(2)}</td>
                        </>
                      )}
                      {(showBomEndUserPrice && hasEndUserPricing) && (
                        <>
                          <td className="py-3 px-2 text-right bg-orange-50"></td>
                          <td className="py-3 px-2 text-right font-bold bg-orange-50 text-orange-700">£{result.installer_delivery?.toFixed(2)}</td>
                        </>
                      )}
                    </tr>
                    <tr>
                      <td colSpan={5 + (showBomInstallerPrice && hasInstallerPricing && !installerOnlyCapture ? 2 : 0) + (showBomEndUserPrice && hasEndUserPricing ? 2 : 0) + 1} className="py-1 px-2 text-right text-xs text-slate-500 italic">
                        (Delivery surcharges for multiple units may vary - please check with your ZKTeco Rep.)
                      </td>
                    </tr>
                    <tr className="border-t-2 bg-orange-100 font-bold">
                      <td colSpan="4" className="py-3 px-2 text-right text-lg">
                        {installerOnlyCapture ? 'Installer Total:' : showBomDistributorPrice ? 'Distributor Total:' : userRole === 'installer' ? 'Your Total:' : 'Total:'}
                      </td>
                      <td className="py-3 px-2 text-right text-xl">£{(installerOnlyCapture ? result.installer_total : result.total)?.toFixed(2)}</td>
                      {(showBomInstallerPrice && hasInstallerPricing && !installerOnlyCapture) && (
                        <>
                          <td className="py-3 px-2 text-right text-lg bg-green-50 text-green-800">{userRole === 'installer' ? 'Your Total:' : 'Installer Total:'}</td>
                          <td className="py-3 px-2 text-right text-xl bg-green-50 text-green-700 font-bold">£{result.installer_total?.toFixed(2)}</td>
                        </>
                      )}
                      {(showBomEndUserPrice && hasEndUserPricing) && (
                        <>
                          <td className="py-3 px-2 text-right text-lg bg-orange-50 text-orange-800">End User Total:</td>
                          <td className="py-3 px-2 text-right text-xl bg-orange-50 text-orange-700 font-bold">
                            £{(result.bom?.reduce((sum, item) => sum + (item.end_user_line_total || 0), 0) + (result.installer_delivery || 0)).toFixed(2)}
                          </td>
                        </>
                      )}
                    </tr>
                    {sundryItems.length > 0 && (
                      <>
                        <tr className="bg-amber-50">
                          <td colSpan={4 + (showBomInstallerPrice && hasInstallerPricing && !installerOnlyCapture ? 2 : 0) + (showBomEndUserPrice && hasEndUserPricing ? 1 : 0)} className="py-3 px-2 text-right font-semibold text-amber-800">Sundry Items Total:</td>
                          <td className="py-3 px-2 text-right font-bold text-amber-800">£{calculateSundryTotal(sundryItems).toFixed(2)}</td>
                        </tr>
                        <tr className="bg-green-100 border-t-2 border-green-400">
                          <td colSpan={4 + (showBomInstallerPrice && hasInstallerPricing && !installerOnlyCapture ? 2 : 0) + (showBomEndUserPrice && hasEndUserPricing ? 1 : 0)} className="py-3 px-2 text-right font-bold text-green-900">Grand Total (incl. Sundry):</td>
                          <td className="py-3 px-2 text-right font-bold text-green-900 text-xl">
                            £{showBomEndUserPrice && hasEndUserPricing
                              ? ((result.bom?.reduce((sum, item) => sum + (item.end_user_line_total || 0), 0) + (result.installer_delivery || 0)) + calculateSundryTotal(sundryItems)).toFixed(2)
                              : ((result.installer_total || result.total || 0) + calculateSundryTotal(sundryItems)).toFixed(2)}
                          </td>
                        </tr>
                      </>
                    )}
                    {result.distributor_margin && showBomDistributorPrice && (
                      <tr className="bg-green-50">
                        <td colSpan={4 + (showBomInstallerPrice && hasInstallerPricing && !installerOnlyCapture ? 2 : 0)} className="py-3 px-2 text-right font-semibold text-green-800">Your Margin:</td>
                        <td className="py-3 px-2 text-right font-bold text-green-800 text-lg">
                          £{result.distributor_margin?.toFixed(2)}
                          <span className="text-sm ml-2">
                            ({result.margin_percentage?.toFixed(1)}%)
                          </span>
                        </td>
                      </tr>
                    )}
                  </tfoot>
                </table>
              </div>
            </CardContent>
          </Card>
          </div>
        )}

        {/* Project Support Dialog */}
        <ProjectSupportDialog
          open={showProjectSupport}
          onOpenChange={(open) => {
            setShowProjectSupport(open);
            // If user closes dialog without submitting, mark as declined
            if (!open) {
              setSupportDeclined(true);
            }
          }}
          quoteData={{
            project_name: projectName,
            quote_reference: quoteReference,
            installer_total: result?.installer_total
          }}
          configuratorType="parking_barrier"
        />

        {/* Sundry Item Dialog */}
        <Dialog open={showSundryDialog} onOpenChange={setShowSundryDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>{editingSundryIndex !== null ? 'Edit Sundry Item' : 'Add Sundry Item'}</DialogTitle>
              <DialogDescription>
                Add custom line items such as labour, cabling, commissioning, travel, etc.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="sundry-sku">SKU (optional)</Label>
                <Input
                  id="sundry-sku"
                  placeholder="e.g. LABOUR-001"
                  value={sundryForm.sku}
                  onChange={(e) => setSundryForm({...sundryForm, sku: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="sundry-description">Description *</Label>
                <Input
                  id="sundry-description"
                  placeholder="e.g. Installation labour, Cabling, Travel"
                  value={sundryForm.description}
                  onChange={(e) => setSundryForm({...sundryForm, description: e.target.value})}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="sundry-quantity">Quantity *</Label>
                  <Input
                    id="sundry-quantity"
                    type="number"
                    min="0.01"
                    step="0.01"
                    placeholder="1"
                    value={sundryForm.quantity}
                    onChange={(e) => setSundryForm({...sundryForm, quantity: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="sundry-price">Unit Price (£) *</Label>
                  <Input
                    id="sundry-price"
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    value={sundryForm.unit_price}
                    onChange={(e) => setSundryForm({...sundryForm, unit_price: e.target.value})}
                  />
                </div>
              </div>
              {sundryForm.quantity && sundryForm.unit_price && (
                <div className="bg-slate-50 p-3 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-600">Line Total:</span>
                    <span className="font-semibold">
                      £{((parseFloat(sundryForm.quantity) || 0) * (parseFloat(sundryForm.unit_price) || 0)).toFixed(2)}
                    </span>
                  </div>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowSundryDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleSaveSundryItem}>
                {editingSundryIndex !== null ? 'Update' : 'Add Item'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default ParkingBarriers;